import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Dialog, Props as DialogProps } from '../Dialog/Dialog';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { PageLayout } from '../PageLayout/PageLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailSchema } from '~/utils/validation';
import { useDialog } from '~/hooks/useDialog';
import { Fragment } from 'react/jsx-runtime';
import { Button } from '../Button/Button';
import { useForm } from 'react-hook-form';
import { Table } from '../Table/Table';
import { ListAdmin } from '~/apis/ssf';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Input } from '../Input';
import { z } from 'zod';

interface MutationPayload {
  id: number;
  payload: { email: string };
}

interface Props {
  removeMutation: UseMutationResult<unknown, Error, MutationPayload>;
  createMutation: UseMutationResult<unknown, Error, MutationPayload>;
  query: UseQueryResult<ListAdmin[]>;
  showRemove: boolean;
  showCreate: boolean;
  entityId: number;
  title: string;
}

const schema = z.object({
  email: emailSchema,
});

type Schema = z.infer<typeof schema>;

function ListTable({
  showRemove,
  query,
  onRemove,
}: Pick<Props, 'showRemove' | 'query'> & { onRemove: (email: string) => void }) {
  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.Col as="th">E-post</Table.Col>
          <Table.Col as="th">Namn</Table.Col>
          {showRemove && <Table.Col as="th" />}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {query.isLoading ? (
          <Table.Loading cols={showRemove ? 2 : 1} />
        ) : (query.data?.length ?? 0) > 0 ? (
          query.data?.map(admin => (
            <Table.Row key={admin.id}>
              <Table.Col>{admin.email ?? '-'}</Table.Col>
              <Table.Col>{[admin.first_name, admin.last_name].filter(Boolean).join(' ') || '-'}</Table.Col>
              {showRemove && (
                <Table.Col align="right">
                  <Table.Context
                    items={[
                      <ContextMenu.Item label="Ta bort" onClick={() => onRemove(admin.email!)} key="remove" danger />,
                    ]}
                  />
                </Table.Col>
              )}
            </Table.Row>
          ))
        ) : (
          <Table.Empty colSpan={showRemove ? 3 : 2} text="Föreningen har för tillfället inga administratörer" />
        )}
      </Table.Body>
    </Table>
  );
}

function CreateDialog({
  isOpen,
  createMutation,
  entityId,
  onClose,
}: DialogProps & Pick<Props, 'entityId' | 'createMutation'>) {
  const { formState, register, handleSubmit, setError } = useForm<Schema>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(payload: Schema) {
    try {
      await createMutation.mutateAsync({ id: entityId, payload });
      onClose();
    } catch (error) {
      if (isAxiosError(error) && error.status === 404) {
        setError('email', {
          message: 'Det finns ingen registrerad användare med denna e-post adress',
          type: 'custom',
        });
      } else {
        throw error;
      }
    }
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Dialog.Title>Lägg till administratör</Dialog.Title>

        <Input.Group formState={formState} label="E-post" htmlFor="email">
          <Input.Field formState={formState} type="email" {...register('email')} />
        </Input.Group>

        <Button.Group align="right">
          <Button variant="secondary" type="button" onClick={onClose}>
            Avbryt
          </Button>
          <Button variant="primary" type="submit" isLoading={createMutation.isPending}>
            Lägg till
          </Button>
        </Button.Group>
      </form>
    </Dialog>
  );
}

export function GenericManageAdminPage(props: Props) {
  const { entityId, createMutation, removeMutation, query, showCreate, showRemove, title } = props;
  const [pendingRemoveEmail, setPendingRemoveEmail] = useState<string>();

  const createDialog = useDialog();

  async function handleConfirmDelete() {
    await removeMutation.mutateAsync({ id: entityId, payload: { email: pendingRemoveEmail! } });
    setPendingRemoveEmail(undefined);
  }

  return (
    <Fragment>
      <PageLayout.Header>
        <PageLayout.Heading text={title} />

        {showCreate && (
          <Button type="button" onClick={createDialog.open}>
            Skapa ny
          </Button>
        )}
      </PageLayout.Header>

      <PageLayout.Section>
        <ListTable onRemove={email => setPendingRemoveEmail(email)} showRemove={showRemove} query={query} />
      </PageLayout.Section>

      <ConfirmDialog
        onClose={() => setPendingRemoveEmail(undefined)}
        isLoading={removeMutation.isPending}
        onConfirm={handleConfirmDelete}
        isOpen={!!pendingRemoveEmail}
      />

      <CreateDialog entityId={entityId} createMutation={createMutation} {...createDialog} />
    </Fragment>
  );
}
